import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { apiUrl } from "../../utils/api";
import {
    verflechtungAusbauen,
    verflechtungStand,
    verflechtungUebernehmen,
    type VerflechtungBericht,
    type VerflechtungSchritt,
    type VerflechtungUebernahme,
    type VerflechtungVorschlag,
} from "../../services/firmendatenService";
import {
    entwuerfeAus,
    gruppenBauen,
    unvollstaendig,
    artNoetig,
    vorauswahl,
    zusammenfuehren,
    type Entwurf,
} from "./verflechtungEntwuerfe";
import { FIRMEN_ROLLEN, FIRMEN_ROLLE_HINWEIS, FIRMEN_ROLLE_LABEL } from "../../utils/firmenGesellschafterArt";

/**
 * Der Ausbau der Beteiligungskette — sammeln, prüfen, bestätigen.
 *
 * **Warum ein eigenes Fenster.** Ein Portalvorgang dauert 10 bis 30 Sekunden,
 * und eine Kette hat schnell vier davon. Als ein einziger Aufruf fühlt sich das
 * an, als hinge die Anwendung. Hier steht Zeile für Zeile, welche Gesellschaft
 * gerade abgerufen wird und was dabei herauskam.
 *
 * **Warum drei Schritte.** Bis 09/2026 hat der Lauf jeden Fund sofort als
 * Kontakt angelegt. Über zwei Ebenen entstanden dutzende Kontakte, die niemand
 * vorher gesehen hatte, und zurückzunehmen war jeder einzeln über den
 * Papierkorb. Seitdem sammelt der Lauf nur; der Anwender sieht die Vorschläge,
 * korrigiert Anrede und Schreibweise, wählt ab, was nicht stimmt, und bestätigt
 * erst dann. Vorbild ist der `RegisterBeteiligteDialog`.
 *
 * **Wie der Fortschritt zustande kommt.** Der Dienst ist wiederaufsetzbar: Er
 * leitet seine Warteschlange jedes Mal aus den Daten ab und meldet, was noch
 * offen ist. Statt eines langen Aufrufs laufen viele kurze, jeder mit dem
 * Budget für genau eine Gesellschaft. Zwischen zwei Aufrufen ist der
 * Fortschritt sichtbar und der Abbruch möglich.
 *
 * **Warum es eine zweite Ebene erst nach dem Bestätigen gibt.** Die
 * Gesellschafterliste einer GmbH ist ein Dokument im Registerordner und wird in
 * der Akte abgelegt — dafür braucht es einen Kontakt. Eine gerade erst
 * vorgeschlagene Gesellschaft hat keinen. Der Lauf reicht deshalb genau so
 * tief, wie bereits verbundene Gesellschafter reichen; nach dem Bestätigen ist
 * das eine Ebene mehr, und „Nächste Ebene ausbauen" holt sie.
 *
 * Die Optik kommt aus `contactRedesign.css` (kvp-rd) plus makeStyles — der
 * Dialog hängt per Portal am `document.body` und damit außerhalb von `.kvp-rd`,
 * wo die gescopten Regeln nicht greifen.
 */

/**
 * Vorgänge je Aufruf. Zwei, weil eine Gesellschaft zwei kostet — die
 * Gesellschafterliste aus dem Registerordner und die Leitungsorgane aus dem
 * Registerinhalt. Mit nur einem käme der zweite Schritt nie dran.
 */
const VORGAENGE_JE_RUNDE = 2;

/** Reißleine gegen eine Kette, die sich nicht erschöpft. */
const MAX_RUNDEN = 25;
/**
 * Wie oft je Runde nachgefragt werden darf, solange das Backend „läuft"
 * meldet. Jede Nachfrage wartet dort bis zu 15 Sekunden — 20 Versuche sind
 * also fünf Minuten für EINEN Durchgang. Wer länger braucht, hängt.
 */
const MAX_NACHFRAGEN = 20;

/** So viele Bestätigungsrunden bietet der Dialog an (Ebene 0 und Ebene 1). */
const MAX_EBENEN = 2;

const useStyles = makeStyles({
    einleitung: { fontSize: "13px", color: "#6b5a66", marginBottom: "14px", lineHeight: 1.5 },
    liste: { maxHeight: "44vh", overflowY: "auto", overflowX: "hidden" },
    zeile: {
        display: "flex",
        alignItems: "flex-start",
        ...shorthands.gap("10px"),
        ...shorthands.padding("9px", "2px"),
        ...shorthands.borderBottom("1px", "solid", "#ece6ec"),
    },
    auswahlZeile: { cursor: "pointer" },
    marke: {
        flexShrink: 0,
        width: "18px",
        textAlign: "center",
        fontSize: "13px",
        lineHeight: 1.35,
    },
    inhalt: { flexGrow: 1, minWidth: 0 },
    name: {
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "14px",
        fontWeight: 700,
        color: "#241522",
        lineHeight: 1.3,
    },
    meta: { display: "block", fontSize: "12.5px", color: "#6b5a66", marginTop: "2px" },
    laeuft: { color: "#7d2169", fontWeight: 700 },
    fehlerText: { color: "#b42318" },
    warnung: {
        fontSize: "12.5px",
        color: "#7a5a18",
        backgroundColor: "#fdf6e3",
        ...shorthands.padding("9px", "11px"),
        ...shorthands.borderRadius("8px"),
        marginTop: "12px",
        lineHeight: 1.45,
    },
    standHinweis: {
        fontSize: "12.5px",
        color: "#1f4f7a",
        backgroundColor: "#eef5fb",
        ...shorthands.padding("9px", "11px"),
        ...shorthands.borderRadius("8px"),
        marginBottom: "12px",
        lineHeight: 1.45,
    },
    /** Ein schmaler Balken, der ohne Prozentangabe auskommt — wir kennen sie nicht. */
    balken: {
        height: "3px",
        backgroundColor: "#f0e6ee",
        ...shorthands.borderRadius("999px"),
        overflow: "hidden",
        marginBottom: "14px",
    },
    balkenLauf: {
        height: "100%",
        width: "38%",
        backgroundColor: "#7d2169",
        ...shorthands.borderRadius("999px"),
        animationName: {
            "0%": { transform: "translateX(-100%)" },
            "100%": { transform: "translateX(320%)" },
        },
        animationDuration: "1.6s",
        animationIterationCount: "infinite",
        animationTimingFunction: "ease-in-out",
    },
    knopf: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("8px"),
        ...shorthands.padding("9px", "14px"),
        ...shorthands.borderRadius("9px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        backgroundColor: "#fff",
        color: "#7d2169",
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "12.5px",
        fontWeight: 700,
        lineHeight: 1,
        cursor: "pointer",
        whiteSpace: "nowrap",
        transitionDuration: "130ms",
        ":hover": { ...shorthands.borderColor("#cd8fbd"), backgroundColor: "#f8eef5" },
        ":disabled": { color: "#a596a1", cursor: "default", backgroundColor: "#f6f3f5" },
    },
    knopfStark: {
        backgroundColor: "#7d2169",
        color: "#fff",
        ...shorthands.borderColor("#7d2169"),
        ":hover": { backgroundColor: "#591149", ...shorthands.borderColor("#591149") },
        ":disabled": {
            backgroundColor: "#d9c6d4",
            ...shorthands.borderColor("#d9c6d4"),
            color: "#fff",
        },
    },
    werkzeuge: {
        display: "flex",
        columnGap: "10px",
        rowGap: "8px",
        alignItems: "center",
        flexWrap: "wrap",
        marginBottom: "12px",
    },
    zaehler: { fontSize: "13px", color: "#6b5a66" },
    gruppeTitel: {
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#8b7a86",
        marginTop: "14px",
        marginBottom: "4px",
        display: "block",
    },
    formZeile: {
        display: "grid",
        gridTemplateColumns:
            "minmax(120px, 1fr) minmax(140px, 1.4fr) minmax(140px, 1.4fr) minmax(140px, 1fr)",
        ...shorthands.gap("10px"),
        alignItems: "end",
        ...shorthands.padding("10px", "0"),
        ...shorthands.borderBottom("1px", "solid", "#ece6ec"),
        "@media (max-width: 720px)": { gridTemplateColumns: "1fr 1fr" },
    },
    feld: { display: "flex", flexDirection: "column", ...shorthands.gap("4px"), minWidth: 0 },
    feldBreit: { gridColumnStart: 1, gridColumnEnd: -1 },
    beschriftung: { fontSize: "11.5px", fontWeight: 700, color: "#6b5a66" },
    eingabe: {
        ...shorthands.padding("9px", "10px"),
        ...shorthands.borderRadius("9px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "13.5px",
        fontWeight: 600,
        color: "#241522",
        backgroundColor: "#fff",
        minWidth: 0,
        ":focus": { ...shorthands.borderColor("#7d2169"), outlineStyle: "none" },
    },
    haken: { marginTop: "4px" },
    kennung: { fontSize: "11.5px", fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 },
    kennungNeu: { color: "#7d2169" },
    kennungVorhanden: { color: "#1f7a4d" },
    kennungAehnlich: { color: "#a8620a" },
    ergebnis: { fontSize: "14px", color: "#241522" },
    ergebnisFehler: { color: "#b42318", marginTop: "8px", marginLeft: "18px" },
    fuss: { display: "flex", ...shorthands.gap("10px"), justifyContent: "flex-end" },
});

/** Zeichen je Ausgang — knapp, weil die Liste lang wird. */
const MARKE: Record<VerflechtungSchritt["status"], string> = {
    ausgebaut: "✓",
    uebersprungen: "·",
    "keine-liste": "–",
    "ohne-register": "!",
    "wird-gelesen": "…",
    fehler: "×",
};

const beschreibung = (s: VerflechtungSchritt): string => {
    if (s.status === "ausgebaut") {
        return `${s.gefunden ?? 0} gefunden` + (s.meldung ? ` · ${s.meldung}` : "");
    }
    return s.meldung ?? s.status;
};

const uhrzeit = (iso: string | undefined) =>
    iso ? new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "";

type Schritt = "lauf" | "pruefen" | "ergebnis";

export function VerflechtungDialog({
    contactId,
    onClose,
    onFertig,
}: {
    contactId: number;
    onClose: () => void;
    /** Nach dem Bestätigen: Detailansicht neu laden (neue Verbindungen). */
    onFertig?: () => void;
}) {
    const s = useStyles();
    const [schritt, setSchritt] = useState<Schritt>("lauf");
    const [schritte, setSchritte] = useState<VerflechtungSchritt[]>([]);
    const [vorschlaege, setVorschlaege] = useState<VerflechtungVorschlag[]>([]);
    const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());
    const [entwuerfe, setEntwuerfe] = useState<Record<string, Entwurf>>({});
    const [bericht, setBericht] = useState<VerflechtungBericht | null>(null);
    const [ergebnis, setErgebnis] = useState<VerflechtungUebernahme | null>(null);
    const [aktuell, setAktuell] = useState<string | null>(null);
    const [laeuft, setLaeuft] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const [anreden, setAnreden] = useState<string[]>(["Herr", "Frau"]);
    /** Die wievielte Bestätigungsrunde läuft (0 = die erste Ebene). */
    const [ebene, setEbene] = useState(0);
    /** Der Prüfen-Schritt zeigt einen bereits bezahlten Lauf von vorhin. */
    const [aufgenommen, setAufgenommen] = useState(false);
    /** Wie viele Portalvorgänge diese Sitzung schon gekostet hat. */
    const [vorgaenge, setVorgaenge] = useState(0);

    /**
     * Der Abbruchwunsch als Ref, nicht als State: Die Schleife liest ihn
     * zwischen zwei Aufrufen, und ein State-Wert wäre in der laufenden
     * Closure eingefroren.
     */
    const abbrechen = useRef(false);
    const gestartet = useRef(false);

    useEffect(() => {
        // Anreden aus dem Katalog; der Dialog bleibt ohne sie benutzbar.
        fetch(apiUrl("contactsalutation"))
            .then((r) => (r.ok ? r.json() : null))
            .then((rows: Array<{ salutation?: string }> | null) => {
                const werte = (rows ?? []).map((r) => r?.salutation).filter(Boolean) as string[];
                if (werte.length) setAnreden(werte);
            })
            .catch(() => undefined);
    }, []);

    // Entwürfe folgen der Auswahl. Als Effekt statt als Nebenwirkung im
    // Umschalten: `entwuerfeAus` ist rein und behält getippte Korrekturen,
    // damit ein Haken-Klick keine Handarbeit löscht.
    useEffect(() => {
        setEntwuerfe((stand) => entwuerfeAus(vorschlaege, gewaehlt, stand));
    }, [vorschlaege, gewaehlt]);

    const laufen = useCallback(
        async (neuStarten: boolean) => {
            abbrechen.current = false;
            setLaeuft(true);
            setFehler(null);
            setSchritt("lauf");
            setAufgenommen(false);
            if (neuStarten) {
                setSchritte([]);
                setVorschlaege([]);
                setGewaehlt(new Set());
                setEntwuerfe({});
                setVorgaenge(0);
            }
            // Der Zwischenstand wird nur beim ALLERERSTEN Aufruf weggeworfen —
            // eine Nachfrage („läuft noch") darf nicht löschen, was frühere
            // Runden derselben Sitzung schon bezahlt haben.
            let verwerfen = neuStarten;
            let gefunden: VerflechtungVorschlag[] = [];
            let voriges = -1;
            let nachfragen = 0;
            try {
                for (let runde = 0; runde < MAX_RUNDEN; runde++) {
                    const r = await verflechtungAusbauen(contactId, {
                        tiefe: MAX_EBENEN,
                        maxVorgaenge: VORGAENGE_JE_RUNDE,
                        verwerfen,
                    });
                    verwerfen = false;
                    // Der Durchgang arbeitet noch: Jeder Registerabruf dauert 10
                    // bis 30 Sekunden, und der Proxy dazwischen legt nach 30 auf.
                    // Das Backend antwortet deshalb rechtzeitig mit „läuft" und
                    // führt den Durchgang im Hintergrund weiter — hier wird nur
                    // nachgefragt. Das ist KEINE Runde: Es wurde nichts abgerufen,
                    // und der Rundenzähler ist das Kontingent-Budget.
                    if (r.status === "laeuft") {
                        if (abbrechen.current) break;
                        nachfragen += 1;
                        if (nachfragen > MAX_NACHFRAGEN) {
                            throw new Error(
                                "Der Registerabruf dauert ungewöhnlich lange. Gefundenes " +
                                    "bleibt erhalten — bitte gleich noch einmal ausbauen.",
                            );
                        }
                        runde -= 1;
                        continue;
                    }
                    nachfragen = 0;
                    setBericht(r);
                    // Die Portalvorgänge sind BEIM LAUF bezahlt, nicht beim
                    // Bestätigen — ein verworfener Lauf hat also schon gekostet.
                    // Deshalb steht die Summe im Fenster.
                    setVorgaenge((alt) => alt + r.vorgaenge);
                    // Stillstand erkennen: Wenn eine Runde weder etwas gefunden
                    // noch die offene Liste verkürzt hat, bringt die nächste auch
                    // nichts — sie würde nur Kontingent kosten.
                    const fortschritt =
                        r.schritte.some((x) => x.status === "ausgebaut") ||
                        r.offen.length !== voriges;
                    voriges = r.offen.length;
                    // Der Dienst liefert je Aufruf den Bericht dieses Aufrufs —
                    // Schritte und Vorschläge werden hier aufsummiert, damit die
                    // Liste die ganze Sitzung zeigt und nicht nur die letzte Runde.
                    setSchritte((alt) => [
                        ...alt,
                        ...r.schritte.filter((neu) => !alt.some((a) => a.kontaktId === neu.kontaktId)),
                    ]);
                    gefunden = zusammenfuehren(gefunden, r.vorschlaege);
                    setVorschlaege(gefunden);
                    // Was als Nächstes drankommt, weiß nur die offene Liste — sie
                    // ist die Vorschau für die Zeile „wird abgerufen".
                    setAktuell(r.offen[0]?.name ?? null);
                    if (r.kontingentErschoepft || r.offen.length === 0 || !fortschritt) break;
                    if (abbrechen.current) break;
                }
            } catch (e) {
                setFehler(e instanceof Error ? e.message : String(e));
            } finally {
                setAktuell(null);
                setLaeuft(false);
                // Gefundenes gehört geprüft; ohne Fund gibt es nichts zu
                // bestätigen und der Bericht ist schon das Ergebnis.
                setGewaehlt(gefunden.length ? vorauswahl(gefunden) : new Set());
                setSchritt(gefunden.length ? "pruefen" : "ergebnis");
            }
        },
        [contactId],
    );

    useEffect(() => {
        // Nur einmal starten — im StrictMode läuft der Effekt sonst doppelt und
        // verbrennt das Stundenkontingent gleich zweimal.
        if (gestartet.current) return;
        gestartet.current = true;
        void (async () => {
            try {
                // Ein Lauf bezahlt seine Portalvorgänge sofort. Liegt von
                // vorhin noch ein unbestätigter Stand, wird er aufgenommen,
                // statt alles noch einmal abzurufen.
                const { bericht: stand } = await verflechtungStand(contactId);
                if (stand && stand.vorschlaege.length) {
                    setBericht(stand);
                    setSchritte(stand.schritte);
                    setVorschlaege(stand.vorschlaege);
                    setGewaehlt(vorauswahl(stand.vorschlaege));
                    setVorgaenge(stand.vorgaenge);
                    setAufgenommen(true);
                    setLaeuft(false);
                    setSchritt("pruefen");
                    return;
                }
            } catch {
                // Kein Stand abrufbar — dann eben laufen lassen.
            }
            void laufen(false);
        })();
    }, [contactId, laufen]);

    const umschalten = (k: string) =>
        setGewaehlt((alt) => {
            const neu = new Set(alt);
            if (neu.has(k)) neu.delete(k);
            else neu.add(k);
            return neu;
        });

    const setzeEntwurf = (
        k: string,
        feld: "anrede" | "vorname" | "nachname" | "geburtsdatum" | "anteil" | "firmenRolle",
        wert: string,
    ) => setEntwuerfe((alt) => ({ ...alt, [k]: { ...alt[k], [feld]: wert } }));

    const bestaetigen = async () => {
        setLaeuft(true);
        setFehler(null);
        try {
            const r = await verflechtungUebernehmen(
                contactId,
                gruppenBauen(vorschlaege, gewaehlt, entwuerfe),
            );
            setErgebnis(r);
            setSchritt("ergebnis");
            if (r.angelegt.length) onFertig?.();
        } catch (e) {
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaeuft(false);
        }
    };

    const naechsteEbene = () => {
        setEbene((e) => e + 1);
        setErgebnis(null);
        void laufen(true);
    };

    /** Die Vorschläge nach Gesellschaft gebündelt — so wird auch bestätigt. */
    const gruppiert = useMemo(() => {
        const nach = new Map<
            number,
            { name: string; tiefe: number; eintraege: VerflechtungVorschlag[] }
        >();
        for (const v of vorschlaege) {
            const g = nach.get(v.firmaId) ?? { name: v.firmaName, tiefe: v.tiefe, eintraege: [] };
            g.eintraege.push(v);
            nach.set(v.firmaId, g);
        }
        return [...nach.entries()];
    }, [vorschlaege]);

    const kennung = (v: VerflechtungVorschlag) =>
        v.anzeige.abgleich === "vorhanden"
            ? { text: "Kontakt vorhanden", klasse: s.kennungVorhanden }
            : v.anzeige.abgleich === "aehnlich"
                ? { text: "Namensgleich — bitte prüfen", klasse: s.kennungAehnlich }
                : { text: "neu", klasse: s.kennungNeu };

    const zurPruefung = bericht?.zurPruefung ?? [];
    const offen = bericht?.offen ?? [];
    const gesperrt = unvollstaendig(vorschlaege, gewaehlt, entwuerfe);

    return createPortal(
        <div className="kvp-rd-scrim" onClick={laeuft ? undefined : onClose}>
            <div className="dm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="dm-head">
                    <div className="dm-head-top">
                        <div className="dm-titles">
                            <div className="dm-kicker">
                                {schritt === "pruefen"
                                    ? "Handelsregister · Verflechtung prüfen"
                                    : "Handelsregister · Verflechtung"}
                            </div>
                            <h2>{bericht?.wurzel.name || "Beteiligungskette wird verfolgt"}</h2>
                        </div>
                        {!laeuft && (
                            <button className="dm-x" onClick={onClose} aria-label="Schließen">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M4.5 4.5l9 9M13.5 4.5l-9 9" /></svg>
                            </button>
                        )}
                    </div>
                </div>

                <div className="dm-body">
                    {laeuft && schritt === "lauf" && (
                        <div className={s.balken} aria-hidden>
                            <div className={s.balkenLauf} />
                        </div>
                    )}

                    {schritt === "lauf" && (
                        <>
                            <div className={s.einleitung}>
                                Gesellschafter der Gesellschafter werden nachverfolgt. Jeder Schritt ist ein
                                Abruf beim Registerportal und dauert einige Sekunden — <strong>angelegt
                                wird dabei nichts</strong>. Was gefunden wird, kommt danach zum Prüfen.
                            </div>
                            <div className={s.liste}>
                                {schritte.map((x) => (
                                    <div key={`${x.kontaktId}-${x.tiefe}`} className={s.zeile}>
                                        <span className={s.marke} aria-hidden>{MARKE[x.status]}</span>
                                        <span className={s.inhalt}>
                                            <span className={s.name}>
                                                {"— ".repeat(x.tiefe)}
                                                {x.name}
                                            </span>
                                            <span
                                                className={mergeClasses(
                                                    s.meta,
                                                    x.status === "fehler" ? s.fehlerText : undefined,
                                                )}
                                            >
                                                {beschreibung(x)}
                                            </span>
                                        </span>
                                    </div>
                                ))}
                                {laeuft && (
                                    <div className={s.zeile}>
                                        <span className={s.marke} aria-hidden>›</span>
                                        <span className={s.inhalt}>
                                            <span className={mergeClasses(s.name, s.laeuft)}>
                                                {aktuell ?? "Registerportal wird befragt …"}
                                            </span>
                                            <span className={s.meta}>wird abgerufen …</span>
                                        </span>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {schritt === "pruefen" && (
                        <>
                            {aufgenommen && (
                                <div className={s.standHinweis}>
                                    Diese Vorschläge stammen aus dem Lauf von {uhrzeit(bericht?.gesammeltAm)} Uhr
                                    und sind bereits abgerufen — das Bestätigen kostet kein weiteres
                                    Registerkontingent.{" "}
                                    <button
                                        type="button"
                                        className={s.knopf}
                                        onClick={() => void laufen(true)}
                                    >
                                        Verwerfen und neu abrufen
                                    </button>
                                </div>
                            )}
                            <div className={s.einleitung}>
                                Bitte prüfen und bei Bedarf ändern. Anrede und Schreibweise stammen nicht aus
                                dem Register, der Wohnort wird nicht als Adresse übernommen. Bei einem bereits
                                vorhandenen Kontakt bleiben die Stammdaten unberührt — dort wird nur die
                                Verbindung angelegt. Ohne Haken passiert nichts.
                            </div>
                            <div className={s.werkzeuge}>
                                <button
                                    type="button"
                                    className={s.knopf}
                                    onClick={() => setGewaehlt(vorauswahl(vorschlaege))}
                                >
                                    Vorauswahl wiederherstellen
                                </button>
                                <button type="button" className={s.knopf} onClick={() => setGewaehlt(new Set())}>
                                    Auswahl leeren
                                </button>
                                <span className={s.zaehler}>
                                    {gewaehlt.size} von {vorschlaege.length} ausgewählt
                                </span>
                            </div>
                            <div className={s.liste}>
                                {gruppiert.map(([firmaId, g]) => (
                                    <div key={firmaId}>
                                        <span className={s.gruppeTitel}>
                                            {g.name} · Ebene {g.tiefe}
                                        </span>
                                        {g.eintraege.map((v) => {
                                            const k = kennung(v);
                                            const e = entwuerfe[v.schluessel];
                                            const istPerson = v.eintrag.art === "person";
                                            return (
                                                <div key={v.schluessel}>
                                                    <label className={mergeClasses(s.zeile, s.auswahlZeile)}>
                                                        <input
                                                            type="checkbox"
                                                            className={s.haken}
                                                            checked={gewaehlt.has(v.schluessel)}
                                                            onChange={() => umschalten(v.schluessel)}
                                                        />
                                                        <span className={s.inhalt}>
                                                            <span className={s.name}>{v.anzeige.name}</span>
                                                            {!istPerson && (
                                                                <span className={s.zaehler}> (Gesellschaft)</span>
                                                            )}
                                                            <span className={s.meta}>
                                                                {v.anzeige.rolleAmtlich || v.anzeige.rolle}
                                                                {artNoetig(v)
                                                                    ? " · wird verbunden als Mutterfirma oder Beteiligung"
                                                                    : ` · wird verbunden als ${v.anzeige.beziehungstyp}`}
                                                                {v.anzeige.beziehungsfunktion
                                                                    ? ` (${v.anzeige.beziehungsfunktion})`
                                                                    : ""}
                                                                {v.anzeige.ort ? ` · ${v.anzeige.ort}` : ""}
                                                                {v.eintrag.registerNummer
                                                                    ? ` · ${v.eintrag.registerNummer}`
                                                                    : ""}
                                                                {v.zweig === "organ"
                                                                    ? " · aus dem Registerinhalt"
                                                                    : " · aus der Gesellschafterliste"}
                                                            </span>
                                                            {v.grund && (
                                                                <span className={mergeClasses(s.meta, s.fehlerText)}>
                                                                    Nicht vorausgewählt: {v.grund}
                                                                </span>
                                                            )}
                                                        </span>
                                                        <span className={mergeClasses(s.kennung, k.klasse)}>
                                                            {k.text}
                                                        </span>
                                                    </label>
                                                    {e && (
                                                        <div className={s.formZeile}>
                                                            {e.gesperrt ? (
                                                                <span className={mergeClasses(s.feld, s.feldBreit)}>
                                                                    <span className={s.beschriftung}>
                                                                        Vorhandener Kontakt
                                                                    </span>
                                                                    <span className={s.name}>
                                                                        {v.anzeige.kontaktName || e.nachname}
                                                                    </span>
                                                                </span>
                                                            ) : istPerson ? (
                                                                <>
                                                                    <span className={s.feld}>
                                                                        <span className={s.beschriftung}>Anrede</span>
                                                                        <select
                                                                            className={s.eingabe}
                                                                            value={e.anrede}
                                                                            onChange={(ev) =>
                                                                                setzeEntwurf(v.schluessel, "anrede", ev.target.value)
                                                                            }
                                                                        >
                                                                            <option value="">— keine —</option>
                                                                            {anreden.map((a) => (
                                                                                <option key={a} value={a}>{a}</option>
                                                                            ))}
                                                                        </select>
                                                                    </span>
                                                                    <span className={s.feld}>
                                                                        <span className={s.beschriftung}>Vorname</span>
                                                                        <input
                                                                            className={s.eingabe}
                                                                            value={e.vorname}
                                                                            onChange={(ev) =>
                                                                                setzeEntwurf(v.schluessel, "vorname", ev.target.value)
                                                                            }
                                                                        />
                                                                    </span>
                                                                    <span className={s.feld}>
                                                                        <span className={s.beschriftung}>Nachname</span>
                                                                        <input
                                                                            className={s.eingabe}
                                                                            value={e.nachname}
                                                                            onChange={(ev) =>
                                                                                setzeEntwurf(v.schluessel, "nachname", ev.target.value)
                                                                            }
                                                                        />
                                                                    </span>
                                                                    <span className={s.feld}>
                                                                        <span className={s.beschriftung}>Geburtsdatum</span>
                                                                        <input
                                                                            type="date"
                                                                            className={s.eingabe}
                                                                            value={e.geburtsdatum}
                                                                            onChange={(ev) =>
                                                                                setzeEntwurf(v.schluessel, "geburtsdatum", ev.target.value)
                                                                            }
                                                                        />
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <span className={mergeClasses(s.feld, s.feldBreit)}>
                                                                    <span className={s.beschriftung}>
                                                                        Name der Gesellschaft
                                                                    </span>
                                                                    <input
                                                                        className={s.eingabe}
                                                                        value={e.nachname}
                                                                        onChange={(ev) =>
                                                                            setzeEntwurf(v.schluessel, "nachname", ev.target.value)
                                                                        }
                                                                    />
                                                                </span>
                                                            )}
                                                            {/* Der Anteil gehört zur Verbindung, nicht zum
                                                                Kontakt — deshalb auch bei einem vorhandenen
                                                                Kontakt bedienbar. Aus einem gescannten
                                                                Dokument kommt er oft gar nicht mit. */}
                                                            {/* Gesellschaft als schlichte Gesellschafterin: Die
                                                                Art wählt der Anwender (User 02.10.2026). */}
                                                            {artNoetig(v) && (
                                                                <span className={s.feld}>
                                                                    <span className={s.beschriftung}>Verbinden als</span>
                                                                    <select
                                                                        className={s.eingabe}
                                                                        aria-label="Verbinden als"
                                                                        value={e.firmenRolle}
                                                                        onChange={(ev) =>
                                                                            setzeEntwurf(v.schluessel, "firmenRolle", ev.target.value)
                                                                        }
                                                                    >
                                                                        <option value="">— bitte wählen —</option>
                                                                        {FIRMEN_ROLLEN.map((r) => (
                                                                            <option key={r} value={r}>{FIRMEN_ROLLE_LABEL[r]}</option>
                                                                        ))}
                                                                    </select>
                                                                </span>
                                                            )}
                                                            {v.anzeige.beziehungstyp === "Gesellschafter" && (
                                                                <span className={s.feld}>
                                                                    <span className={s.beschriftung}>Anteil</span>
                                                                    <input
                                                                        className={s.eingabe}
                                                                        value={e.anteil}
                                                                        placeholder="z. B. 25,00 %"
                                                                        onChange={(ev) =>
                                                                            setzeEntwurf(v.schluessel, "anteil", ev.target.value)
                                                                        }
                                                                    />
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {schritt === "ergebnis" && (
                        <div className={s.ergebnis}>
                            {ergebnis ? (
                                <>
                                    <p>
                                        {ergebnis.angelegt.length} verbunden
                                        {ergebnis.angelegt.some((a) => a.neu)
                                            ? ` (${ergebnis.angelegt.filter((a) => a.neu).length} neu angelegt)`
                                            : ""}
                                        .
                                    </p>
                                    {ergebnis.fehler.length > 0 && (
                                        <ul className={s.ergebnisFehler}>
                                            {ergebnis.fehler.map((f, i) => (
                                                <li key={i}>{f.name}: {f.grund}</li>
                                            ))}
                                        </ul>
                                    )}
                                </>
                            ) : (
                                <p>
                                    Es gab nichts zu übernehmen — alle gefundenen Beteiligten sind bereits
                                    verbunden, oder es war keine Gesellschafterliste abrufbar.
                                </p>
                            )}
                        </div>
                    )}

                    {schritt === "pruefen" &&
                        vorschlaege.some(
                            (v) => gewaehlt.has(v.schluessel) && artNoetig(v) && !entwuerfe[v.schluessel]?.firmenRolle,
                        ) && (
                            <div className={s.warnung} role="note">{FIRMEN_ROLLE_HINWEIS}</div>
                        )}
                    {fehler && <div className={mergeClasses(s.warnung, s.fehlerText)}>{fehler}</div>}

                    {!laeuft && vorgaenge > 0 && (
                        <div className={s.warnung}>
                            Dieser Lauf hat {vorgaenge}{" "}
                            {vorgaenge === 1 ? "Registerabruf" : "Registerabrufe"} verbraucht — bezahlt
                            wird beim Abrufen, nicht beim Bestätigen.
                            {schritt === "pruefen"
                                ? " Wer die Vorschläge verwirft, bekommt das Kontingent nicht zurück."
                                : ""}
                        </div>
                    )}
                    {!laeuft && bericht?.kontingentErschoepft && (
                        <div className={s.warnung}>
                            Das Stundenkontingent des Registerportals ist erschöpft. Der Rest steht
                            noch aus — später einfach erneut ausbauen, es wird dort weitergemacht.
                        </div>
                    )}
                    {!laeuft && !bericht?.kontingentErschoepft && offen.length > 0 && (
                        <div className={s.warnung}>
                            {offen.length} Gesellschaften stehen noch aus:{" "}
                            {offen.slice(0, 5).map((o) => o.name).join(", ")}
                            {offen.length > 5 ? " …" : ""}
                        </div>
                    )}
                    {schritt !== "pruefen" &&
                        zurPruefung.map((z, i) => (
                            <div key={i} className={s.warnung}>
                                <strong>{z.name}</strong> ({z.firma}): {z.grund}
                            </div>
                        ))}
                </div>

                <div className={mergeClasses("dm-foot", s.fuss)}>
                    {schritt === "lauf" && laeuft && (
                        <button
                            type="button"
                            className={s.knopf}
                            onClick={() => {
                                abbrechen.current = true;
                            }}
                        >
                            Nach diesem Schritt anhalten
                        </button>
                    )}
                    {schritt === "pruefen" && (
                        <button
                            type="button"
                            className={mergeClasses(s.knopf, s.knopfStark)}
                            disabled={laeuft || gesperrt || gewaehlt.size === 0}
                            onClick={() => void bestaetigen()}
                        >
                            {laeuft ? "Wird übernommen …" : `${gewaehlt.size} übernehmen`}
                        </button>
                    )}
                    {schritt === "ergebnis" && !laeuft && ebene + 1 < MAX_EBENEN && (
                        <button
                            type="button"
                            className={mergeClasses(s.knopf, s.knopfStark)}
                            onClick={naechsteEbene}
                        >
                            Nächste Ebene ausbauen
                        </button>
                    )}
                    {!laeuft && (
                        <button type="button" className={s.knopf} onClick={onClose}>
                            {schritt === "ergebnis" ? "Fertig" : "Abbrechen"}
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
